import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { HardDrive, Image as ImageIcon, Database, Upload, Archive, Boxes, Cloud, Sparkles, ShieldCheck, Activity, AlertTriangle, ChevronRight, FileArchive } from "lucide-react";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip as RTooltip } from "recharts";
import { getAdminStorage } from "../lib/api";
import { Loader } from "../components/Loader";

const fmtBytes = (n) => {
    n = Number(n || 0);
    const u = ["B", "KB", "MB", "GB", "TB"];
    let i = 0;
    while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
    return `${i === 0 ? Math.round(n) : n.toFixed(1)} ${u[i]}`;
};

const CAT_META = {
    Images: { Icon: ImageIcon, color: "#9D4CDD" },
    Database: { Icon: Database, color: "#5B8DEF" },
    Uploads: { Icon: Upload, color: "#5BD1B7" },
    Backups: { Icon: Archive, color: "#F6C453" },
    Other: { Icon: Boxes, color: "#8A94A6" },
};
const SEV = {
    high: { color: "#EF5B5B", label: "High" },
    medium: { color: "#F6C453", label: "Medium" },
    low: { color: "#5BD1B7", label: "Low" },
};

const Panel = ({ children, className = "", testId }) => (
    <div data-testid={testId} className={`rounded-2xl bg-[#0E0F16] border border-white/10 shadow-xl shadow-black/40 ${className}`}>{children}</div>
);
const Kicker = ({ children }) => <div className="text-[10px] font-black uppercase tracking-[0.28em] text-[#9D4CDD] mb-1">{children}</div>;

export default function StorageCentre() {
    const { data, isLoading } = useQuery({
        queryKey: ["admin-storage"],
        queryFn: getAdminStorage,
        staleTime: 30_000,
    });

    const chart = useMemo(() => (data?.over_time || []).map((s) => ({
        date: s.date?.slice(5) || "",
        gb: +((s.used_bytes || 0) / 1024 ** 3).toFixed(3),
    })), [data]);

    if (isLoading) return <div className="py-20"><Loader /></div>;
    if (!data) return <div className="rounded-2xl bg-[#0E0F16] border border-white/10 p-10 text-center text-white/60" data-testid="storage-error">Could not load storage data.</div>;

    const t = data.total;
    const perf = data.health?.performance_impact || "Low";
    const perfColor = perf === "High" ? "#EF5B5B" : perf === "Moderate" ? "#F6C453" : "#5BD1B7";

    return (
        <div className="space-y-8 fade-up pb-12 max-w-6xl mx-auto" data-testid="storage-centre-page">
            {/* Hero / total usage */}
            <Panel testId="storage-hero" className="relative overflow-hidden p-6 sm:p-8">
                <div className="absolute -top-24 -right-16 h-72 w-72 rounded-full bg-[#9D4CDD]/20 blur-3xl pointer-events-none" />
                <div className="relative">
                    <div className="flex items-center gap-3 mb-4">
                        <span className="h-11 w-11 rounded-xl bg-[#9D4CDD]/20 border border-[#9D4CDD]/50 flex items-center justify-center">
                            <HardDrive className="h-5 w-5 text-[#C9A6F0]" />
                        </span>
                        <div>
                            <Kicker>Owner · Infrastructure</Kicker>
                            <h1 className="font-heading text-3xl sm:text-4xl font-black uppercase tracking-tight text-white leading-none">Storage Centre</h1>
                        </div>
                    </div>
                    <div className="flex flex-wrap items-end gap-x-3 gap-y-1 mb-3">
                        <span className="font-heading text-4xl sm:text-5xl font-black text-white tabular-nums" data-testid="storage-used">{fmtBytes(t.used_bytes)}</span>
                        <span className="text-white/45 text-lg font-bold pb-1">/ {fmtBytes(t.limit_bytes)} used</span>
                        <span className="ml-auto text-sm font-bold pb-1" style={{ color: t.percent > 85 ? "#EF5B5B" : t.percent > 70 ? "#F6C453" : "#5BD1B7" }} data-testid="storage-percent">{t.percent}%</span>
                    </div>
                    <div className="h-3 rounded-full bg-white/[0.07] overflow-hidden" data-testid="storage-progress">
                        <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, t.percent)}%`, background: "linear-gradient(90deg,#9D4CDD,#5BD1B7)", boxShadow: "0 0 14px #9D4CDD88" }} />
                    </div>
                    <div className="text-[11px] uppercase tracking-wider text-white/40 mt-2">{fmtBytes(t.free_bytes)} free · 5 GB Emergent allocation</div>
                </div>
            </Panel>

            {/* Breakdown by category */}
            <section data-testid="storage-breakdown">
                <Kicker>Where It Lives</Kicker>
                <h2 className="font-heading text-2xl font-black uppercase tracking-tight text-white mb-4">Breakdown By Category</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {data.breakdown.map((b) => {
                        const m = CAT_META[b.category] || CAT_META.Other;
                        return (
                            <Panel key={b.category} testId={`storage-cat-${b.category}`} className="p-4">
                                <div className="flex items-center gap-2.5 mb-3">
                                    <span className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ background: `${m.color}22`, border: `1px solid ${m.color}55` }}>
                                        <m.Icon className="h-4 w-4" style={{ color: m.color }} />
                                    </span>
                                    <span className="font-heading text-base font-black uppercase tracking-tight text-white">{b.category}</span>
                                    <span className="ml-auto font-mono-stat text-sm font-black text-white tabular-nums">{fmtBytes(b.bytes)}</span>
                                </div>
                                <div className="h-2 rounded-full bg-white/[0.07] overflow-hidden mb-2">
                                    <div className="h-full rounded-full" style={{ width: `${b.percent}%`, background: m.color }} />
                                </div>
                                <div className="flex justify-between text-[11px] text-white/45">
                                    <span>{b.files.toLocaleString()} items</span>
                                    <span className="font-mono-stat" style={{ color: m.color }}>{b.percent}%</span>
                                </div>
                            </Panel>
                        );
                    })}
                </div>
            </section>

            {/* Usage over time */}
            <section data-testid="storage-overtime">
                <div className="flex items-end justify-between mb-4">
                    <div>
                        <Kicker>Growth</Kicker>
                        <h2 className="font-heading text-2xl font-black uppercase tracking-tight text-white">Usage Over Time</h2>
                    </div>
                    {data.estimated_history && <span className="text-[10px] uppercase tracking-wider text-white/40 pb-1">estimated · builds daily</span>}
                </div>
                <Panel className="p-4 pt-5">
                    <div className="h-56">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={chart} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="sgrad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#9D4CDD" stopOpacity={0.55} />
                                        <stop offset="100%" stopColor="#9D4CDD" stopOpacity={0.02} />
                                    </linearGradient>
                                </defs>
                                <XAxis dataKey="date" tick={{ fill: "#64748B", fontSize: 10 }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                                <YAxis tick={{ fill: "#64748B", fontSize: 10 }} axisLine={false} tickLine={false} width={44} tickFormatter={(v) => `${v}GB`} />
                                <RTooltip contentStyle={{ background: "#111219", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, fontSize: 12 }} itemStyle={{ color: "#C9A6F0" }} labelStyle={{ color: "#fff" }} formatter={(v) => [`${v} GB`, "Used"]} />
                                <Area type="monotone" dataKey="gb" stroke="#C9A6F0" strokeWidth={2} fill="url(#sgrad)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </Panel>
            </section>

            {/* Largest files */}
            <section data-testid="storage-largest">
                <Kicker>Heavy Hitters</Kicker>
                <h2 className="font-heading text-2xl font-black uppercase tracking-tight text-white mb-4">Largest Stores</h2>
                <Panel className="divide-y divide-white/[0.06]">
                    {data.largest_files.map((f, i) => {
                        const m = CAT_META[f.type] || CAT_META.Other;
                        return (
                            <div key={f.name} className="flex items-center gap-3 px-4 py-3" data-testid={`storage-file-${f.name}`}>
                                <span className="w-5 text-right font-mono-stat text-xs font-bold text-white/30 tabular-nums">{i + 1}</span>
                                <span className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${m.color}1f`, border: `1px solid ${m.color}44` }}>
                                    <FileArchive className="h-4 w-4" style={{ color: m.color }} />
                                </span>
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-bold text-white truncate">{f.name}</div>
                                    <div className="text-[11px] text-white/40">{f.type} · {(f.count || 0).toLocaleString()} records</div>
                                </div>
                                <span className="font-mono-stat text-sm font-black text-white tabular-nums shrink-0">{fmtBytes(f.bytes)}</span>
                            </div>
                        );
                    })}
                </Panel>
            </section>

            {/* Optimisation + External storage */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                <section data-testid="storage-optimisation">
                    <Kicker>Reclaim Space</Kicker>
                    <h2 className="font-heading text-2xl font-black uppercase tracking-tight text-white mb-4">Optimisation</h2>
                    <div className="space-y-3">
                        {data.optimisation.map((s) => {
                            const sev = SEV[s.severity] || SEV.low;
                            return (
                                <Panel key={s.title} testId={`storage-opt-${s.severity}`} className="p-4 flex items-start gap-3">
                                    <span className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0 mt-0.5" style={{ background: `${sev.color}1f`, border: `1px solid ${sev.color}55` }}>
                                        {s.severity === "high" ? <AlertTriangle className="h-4 w-4" style={{ color: sev.color }} /> : <Sparkles className="h-4 w-4" style={{ color: sev.color }} />}
                                    </span>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <span className="font-bold text-white text-sm">{s.title}</span>
                                            <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ color: sev.color, background: `${sev.color}1a` }}>{sev.label}</span>
                                        </div>
                                        <p className="text-xs text-white/55 mt-1 leading-relaxed">{s.detail}</p>
                                        {s.est_savings_bytes > 0 && <div className="text-[11px] text-[#5BD1B7] mt-1 font-mono-stat">~{fmtBytes(s.est_savings_bytes)} reclaimable</div>}
                                    </div>
                                    <button className="shrink-0 mt-1 inline-flex items-center gap-1 rounded-lg border border-white/12 px-2.5 py-1.5 text-[11px] font-bold text-white/70 hover:bg-white/10 hover:text-white transition-colors" data-testid={`storage-opt-action-${s.action}`}>
                                        {s.action} <ChevronRight className="h-3 w-3" />
                                    </button>
                                </Panel>
                            );
                        })}
                    </div>
                </section>

                <section data-testid="storage-external">
                    <Kicker>Scale Out</Kicker>
                    <h2 className="font-heading text-2xl font-black uppercase tracking-tight text-white mb-4">Connect External Storage</h2>
                    <div className="space-y-3">
                        {data.external.map((e) => (
                            <Panel key={e.provider} testId={`storage-ext-${e.provider.replace(/\s/g, "-")}`} className="p-4 flex items-center gap-3">
                                <span className="h-10 w-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                                    <Cloud className="h-5 w-5 text-white/60" />
                                </span>
                                <div className="min-w-0 flex-1">
                                    <div className="font-bold text-white text-sm">{e.provider}</div>
                                    <div className="text-[11px] text-white/40">Not connected · coming soon</div>
                                </div>
                                <button disabled className="shrink-0 rounded-lg border border-white/10 px-3 py-1.5 text-[11px] font-bold text-white/35 cursor-not-allowed" data-testid={`storage-ext-connect-${e.provider.replace(/\s/g, "-")}`}>Connect</button>
                            </Panel>
                        ))}
                        <p className="text-[11px] text-white/35 px-1">External providers are placeholders for a future release.</p>
                    </div>
                </section>
            </div>

            {/* Health strip */}
            <Panel testId="storage-health" className="p-4 flex flex-col sm:flex-row items-stretch gap-3">
                <div className="flex-1 flex items-center gap-3 rounded-xl bg-white/[0.03] px-4 py-3">
                    <ShieldCheck className="h-5 w-5 text-[#5BD1B7] shrink-0" />
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-white/40">Backup Status</div>
                        <div className="text-sm font-bold text-white" data-testid="storage-backup-status">{data.health.backup_status}</div>
                    </div>
                </div>
                <div className="flex-1 flex items-center gap-3 rounded-xl bg-white/[0.03] px-4 py-3">
                    <Activity className="h-5 w-5 shrink-0" style={{ color: perfColor }} />
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-white/40">Performance Impact</div>
                        <div className="text-sm font-bold" style={{ color: perfColor }} data-testid="storage-perf">{perf}</div>
                    </div>
                </div>
                <div className="flex-1 flex items-center gap-3 rounded-xl bg-white/[0.03] px-4 py-3">
                    <Boxes className="h-5 w-5 text-white/60 shrink-0" />
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-white/40">Collections</div>
                        <div className="text-sm font-bold text-white">{data.health.collections}</div>
                    </div>
                </div>
            </Panel>
        </div>
    );
}
